import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, 
  Shield, 
  Activity, 
  Smartphone, 
  Battery, 
  Wifi, 
  Zap, 
  Target, 
  Award,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const Features: React.FC = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const mainFeatures = [
    {
      icon: <Brain className="w-12 h-12" />,
      title: 'AI-Powered Protection',
      description: 'Advanced neural networks analyze impact patterns and adapt protection in real-time',
      details: [
        'Machine learning algorithms trained on millions of impact scenarios',
        'Predictive injury prevention based on movement patterns',
        'Real-time impact force analysis and distribution',
        'Adaptive protection that learns from your playing style'
      ],
      image: '/features/ai-protection.jpg'
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: 'Smart Materials',
      description: 'Revolutionary polymers that adapt their stiffness based on impact force',
      details: [
        'Non-Newtonian fluid technology for variable protection',
        'Ultra-lightweight carbon fiber construction',
        'Temperature-resistant materials for all weather conditions',
        '50% better impact absorption than traditional guards'
      ],
      image: '/features/smart-materials.jpg'
    },
    {
      icon: <Activity className="w-12 h-12" />,
      title: 'Performance Analytics',
      description: 'Comprehensive tracking of your movements, impacts, and performance metrics',
      details: [
        'Real-time movement and impact tracking',
        'Performance trend analysis over time',
        'Injury risk assessment and prevention alerts',
        'Personalized training recommendations'
      ],
      image: '/features/analytics.jpg'
    },
    {
      icon: <Smartphone className="w-12 h-12" />,
      title: 'Mobile Integration',
      description: 'Seamless connection with our mobile app for complete control and insights',
      details: [
        'Real-time data sync and cloud backup',
        'Customizable protection settings',
        'Social features to compete with teammates',
        'Integration with popular fitness apps'
      ],
      image: '/features/mobile-app.jpg'
    }
  ];

  const additionalFeatures = [
    {
      icon: <Battery className="w-8 h-8" />,
      title: 'Long Battery Life',
      description: 'Up to 20 hours of continuous use with wireless charging',
      benefit: 'Never worry about charging during tournaments'
    },
    {
      icon: <Wifi className="w-8 h-8" />,
      title: 'Seamless Connectivity',
      description: 'Bluetooth 5.0 and WiFi for instant data sync',
      benefit: 'Always connected to your performance data'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Instant Response',
      description: 'Sub-millisecond response time for impact detection',
      benefit: 'Protection that reacts faster than you can blink'
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Precision Sensors',
      description: '9-axis IMU and multiple pressure sensors',
      benefit: 'Accurate detection of even the smallest impacts'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Professional Grade',
      description: 'Meets FIFA and professional league standards',
      benefit: 'Approved for use in official competitions'
    }
  ];

  const faqs = [
    {
      question: 'How does the AI protection system work?',
      answer: 'Our AI system uses advanced machine learning algorithms to analyze your movement patterns and predict potential impact scenarios. It then adjusts the material properties of the shin guard in real-time to provide optimal protection for each specific situation.'
    },
    {
      question: 'Is the device waterproof?',
      answer: 'Yes, the Cresento Pro has an IPX7 rating, making it completely waterproof for normal playing conditions, including rain and wet grass. The device can be submerged in water up to 1 meter for 30 minutes.'
    },
    {
      question: 'How long does the battery last?',
      answer: 'The battery provides up to 20 hours of continuous use. With typical usage patterns (2-3 hours per day), you can expect 7-10 days between charges. The wireless charging system makes it convenient to keep your guards powered.'
    },
    {
      question: 'Can I use it in official matches?',
      answer: 'Absolutely! The Cresento Pro meets and exceeds FIFA safety standards and is approved for use in professional leagues worldwide. Many professional players already use our technology.'
    },
    {
      question: 'What sizes are available?',
      answer: 'We offer sizes from XS to XL to fit players of all ages and body types. Our sizing guide in the app helps you find the perfect fit, and we offer a 30-day return policy if you need to exchange sizes.'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-cresento-green-600 to-cresento-green-800 text-white py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Advanced Features</h1>
            <p className="text-xl md:text-2xl opacity-90">
              Discover the cutting-edge technology that makes Cresento the future of athletic protection
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Feature Navigation */}
            <div className="grid md:grid-cols-4 gap-4 mb-12">
              {mainFeatures.map((feature, index) => (
                <motion.button
                  key={feature.title}
                  onClick={() => setActiveFeature(index)}
                  className={`p-6 rounded-xl text-left transition-all ${
                    activeFeature === index
                      ? 'bg-cresento-green-600 text-white shadow-lg'
                      : 'bg-cresento-primary/30 hover:bg-cresento-primary/50 text-white border border-cresento-secondary/30'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className={`text-sm ${
                    activeFeature === index ? 'text-white/90' : 'text-gray-300'
                  }`}>
                    {feature.description}
                  </p>
                </motion.button>
              ))}
            </div>

            {/* Feature Details */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeFeature}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="grid lg:grid-cols-2 gap-12 items-center"
              >
                <div>
                  <h2 className="text-4xl font-bold mb-6 text-white">
                    {mainFeatures[activeFeature].title}
                  </h2>
                  <p className="text-lg text-gray-300 mb-8">
                    {mainFeatures[activeFeature].description}
                  </p>
                  <ul className="space-y-4">
                    {mainFeatures[activeFeature].details.map((detail, index) => (
                      <motion.li
                        key={detail}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-start gap-3"
                      >
                        <div className="w-2 h-2 bg-cresento-green-600 rounded-full mt-2 flex-shrink-0" />
                        <span className="text-gray-300">{detail}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
                <div className="aspect-square bg-gradient-to-br from-cresento-green-100 to-cresento-green-200 rounded-2xl flex items-center justify-center shadow-2xl">
                  <div className="text-cresento-green-600 scale-150">
                    {mainFeatures[activeFeature].icon}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Additional Features Grid */}
      <section className="py-20 bg-cresento-primary/20">
        <div className="container mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-center mb-12 text-white"
          >
            Additional Features
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {additionalFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-cresento-primary/30 border border-cresento-secondary/30 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="text-cresento-green-600 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-3 text-white">{feature.title}</h3>
                <p className="text-gray-300 mb-4">{feature.description}</p>
                <div className="bg-cresento-green-600/20 rounded-lg p-3">
                  <span className="text-cresento-green-400 font-medium">{feature.benefit}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <h2 className="text-4xl font-bold text-center mb-12 text-white">
              Cresento vs Traditional Guards
            </h2>
            <div className="bg-cresento-primary/30 border border-cresento-secondary/30 rounded-2xl shadow-xl overflow-hidden">
              <div className="grid grid-cols-3 divide-x divide-gray-200">
                <div className="p-6 bg-cresento-primary/50">
                  <h3 className="text-xl font-semibold text-white">Feature</h3>
                </div>
                <div className="p-6 bg-cresento-green-600/20">
                  <h3 className="text-xl font-semibold text-cresento-green-400">Cresento Pro</h3>
                </div>
                <div className="p-6 bg-cresento-primary/50">
                  <h3 className="text-xl font-semibold text-white">Traditional Guards</h3>
                </div>
              </div>
              {[
                ['Impact Protection', 'AI-adaptive, 50% better', 'Fixed protection'],
                ['Weight', '180g (ultra-light)', '250-300g (heavy)'],
                ['Battery Life', '20 hours', 'N/A'],
                ['Performance Tracking', 'Real-time analytics', 'None'],
                ['Injury Prevention', 'Predictive AI system', 'Reactive only'],
                ['Customization', 'App-based settings', 'One-size-fits-all'],
                ['Professional Approval', 'FIFA certified', 'Basic compliance']
              ].map((row, index) => (
                <div key={row[0]} className="grid grid-cols-3 divide-x divide-gray-200 border-t border-gray-200">
                  <div className="p-6">
                    <span className="text-gray-300 font-medium">{row[0]}</span>
                  </div>
                  <div className="p-6">
                    <span className="text-cresento-green-400 font-medium">{row[1]}</span>
                  </div>
                  <div className="p-6">
                    <span className="text-gray-400">{row[2]}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-cresento-primary/20">
        <div className="container mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-center mb-12 text-white"
          >
            Frequently Asked Questions
          </motion.h2>
          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="mb-4"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full bg-cresento-primary/30 border border-cresento-secondary/30 rounded-lg p-6 text-left hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-white">{faq.question}</h3>
                    {expandedFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-cresento-green-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-cresento-green-400" />
                    )}
                  </div>
                </button>
                <AnimatePresence>
                  {expandedFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="bg-cresento-primary/30 border border-t-0 border-cresento-secondary/30 rounded-b-lg px-6 pb-6 -mt-2"
                    >
                      <p className="text-gray-300 pt-4 border-t border-cresento-secondary/20">{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;
